/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import LocalizationComponent from '../../../Localization/LocalizationComponent';
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import NodeTouchActionComponent from '../Action/NodeTouchActionComponent';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import ReferencedComponent from '../ComponentReference/ReferencedComponent';
import { IPopupModal, IPopupOptions } from './IPopupModal';
import PopupData from './PopupData';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Component used to display dynamic popup message form. */
@cc._decorator.ccclass
export default class PopupModal extends ReferencedComponent implements IPopupModal
{
    /** Prefab which will be instantiated and used as popup form. */
    @property(cc.Prefab)
    protected prefab: cc.Prefab = null;

    /** Maximum number of concurrent node windows. */
    @property({ type: cc.Integer, min: 1, max: 100 })
    protected nodePoolSize: number = 1;

    /** Node pool for multiple popups. */
    protected nodePool: cc.NodePool = new cc.NodePool();

    protected referenceTag: number = 0;
    protected eventTarget: cc.EventTarget = null;
    protected openWindows: any = {};

    /** @hidden */
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.configureNodePool();

        this.referenceTag = Math.random() * 0xffffffff;
    }

    /** @hidden */
    protected setContexts(): void
    {
        this._localRef.SetContext(LocalizationComponent);
    }

    /** Creates and instantiates NodePool for multiple windows. */
    private configureNodePool(): void
    {
        this.nodePool = new cc.NodePool();

        let poolSize: number = this.nodePoolSize;
        while (poolSize-- > 0)
        {
            this.nodePool.put(cc.instantiate(this.prefab));
        }
    }

    /**
     * Display Popupmodal with options.
     * @param message Message to display to user.
     * @param options Settings for popup form.
     */
    public Show(message: string, options: IPopupOptions = { MessageId: "default" }): void
    {
        if (this.openWindows[options.MessageId])
        {
            // Duplicate window, do not show.
            CLog.warn(`Duplicate window for ID: ${options.MessageId}. Not opening.`, LogTypes.PopupManager, this);
            return;
        }

        let node: cc.Node = this.nodePool.get();

        if (!node) { node = cc.instantiate(this.prefab); }
        node.active = true;

        let popupData: PopupData = node.getComponent(PopupData);
        popupData.ReferenceTag = this.referenceTag;

        if (!popupData) { return; }

        this.node.addChild(node);
        this.openWindows[options.MessageId] = node;

        popupData.Message = message;

        this.applyBasicButtonHandlers(options, popupData);
        Object.assign(popupData, options);
    }

    /**
     * Applies basic handlers to options object.
     * @param options Popup configuration options.
     * @param uuid Node uuid for closing multiple popups.
     */
    private applyBasicButtonHandlers = (options: IPopupOptions, popupData: PopupData): void =>
    {
        let uuid: string = popupData.node.uuid;

        let fnOnOK: () => any = options.FnOnOK;
        options.FnOnOK = () =>
        {
            this.Close(uuid);
            if (fnOnOK) { fnOnOK(); }
        };

        let fnOnCancel: () => any = options.FnOnCancel;
        options.FnOnCancel = () =>
        {
            this.Close(uuid);
            if (fnOnCancel) { fnOnCancel(); }
        };

        // Defaults
        if (!options.Title) { options.Title = ''; }

        let strOk: string = this._localRef.Value.GetStringResource(PopupData.DefaultOkButtonText);
        let strCancel: string = this._localRef.Value.GetStringResource(PopupData.DefaultCancelButtonText);

        if (options.IncludeOkButton !== false) { options.IncludeOkButton = true; }
        if (!options.OkButtonText) { options.OkButtonText = strOk; }

        if (options.IncludeCancelButton !== true) { options.IncludeCancelButton = false; }
        if (!options.CancelButtonText) { options.CancelButtonText = strCancel; }

        if (options.ClickOffToExit === true)
        {
            let touchAction: NodeTouchActionComponent
                = ComponentBase.GetOrAddComponent(popupData.node, NodeTouchActionComponent);

            touchAction.TargetNode = cc.Canvas.instance.node;
            touchAction.Action = this.onCanvasTouchEndHandler.bind(this, uuid);
        }
    }

    /**
     * Canvas touch handler.
     * @param event: Touch event.
     * @param popupInstanceUuid: Uuid of child popup to close.
     */
    private onCanvasTouchEndHandler = (popupInstanceUuid: string, event: cc.Event.EventTouch) =>
    {
        if (event.target)
        {
            let targetNode: cc.Node = event.target;

            let popupData: PopupData = targetNode.getComponent(PopupData);

            if (popupData && popupData.ReferenceTag === this.referenceTag) { return; }
        }

        this.Close(popupInstanceUuid);
    }

    /**
     * Hide specific popup instance.
     * @param popupInstanceUuid Uuid of which popup node to close.
     */
    public Close(popupInstanceUuid: string): void
    {
        let child: cc.Node = this.node.getChildByUuid(popupInstanceUuid);
        let data: PopupData = child.getComponent(PopupData);

        this.openWindows[data.MessageId] = null;
        this.nodePool.put(child);
    }
}
